import { c as _c } from "react/compiler-runtime";
import { Tooltip, centsToUnits } from '@systemeio/ui';
import { PaymentTypesEnum } from 'modules/payment/transactions/enums/payment-types-enum';
import { SubscriptionIntervalTitlesIcu } from 'modules/payment/transactions/enums/subscription-intervals-enum';
import React from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { PricePlanType } from '@systemeio/types';
interface PaymentTooltipProps {
  pricePlan: PricePlanType | null;
  children: React.ReactNode;
  productPriceAmount?: string;
  customRow?: React.ReactNode;
  hiddenInfo?: boolean;
  stayOnHover?: boolean;
}
const InfoRow = t0 => {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-darkblue font-bold">{label}: </span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <p className="text-start">{t1}{children}</p>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const PricePlanTooltip = t0 => {
  const $ = _c(49);
  const {
    pricePlan,
    children,
    hiddenInfo,
    customRow,
    stayOnHover,
    productPriceAmount
  } = t0;
  const {
    t,
    tICU
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  if (!pricePlan) {
    let t1;
    if ($[0] !== children) {
      t1 = <div>{children}</div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const {
    innerName,
    name,
    statementDescriptor,
    type,
    recurringOptions,
    amount,
    currency
  } = pricePlan;
  let t1;
  if ($[2] !== amount || $[3] !== currency || $[4] !== user?.dashboardLocale) {
    t1 = currencyFormat(centsToUnits(amount), currency, user?.dashboardLocale);
    $[2] = amount;
    $[3] = currency;
    $[4] = user?.dashboardLocale;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const formattedAmount = t1;
  let t2;
  if ($[6] !== hiddenInfo || $[7] !== innerName || $[8] !== name || $[9] !== t) {
    t2 = !hiddenInfo ? <InfoRow label={t("dashboard.price_plan.name")}>{name}</InfoRow> : <InfoRow label={t("dashboard.price_plan.inner_name")}>{innerName}</InfoRow>;
    $[6] = hiddenInfo;
    $[7] = innerName;
    $[8] = name;
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== hiddenInfo || $[12] !== statementDescriptor || $[13] !== t) {
    t3 = statementDescriptor && !hiddenInfo && <InfoRow label={t("dashboard.price_plan.statement_descriptor")}>{statementDescriptor}</InfoRow>;
    $[11] = hiddenInfo;
    $[12] = statementDescriptor;
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== hiddenInfo || $[16] !== t || $[17] !== type) {
    t4 = !hiddenInfo && <InfoRow label={t("global.type")}>{t(PaymentTypesEnum[type])}</InfoRow>;
    $[15] = hiddenInfo;
    $[16] = t;
    $[17] = type;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== formattedAmount || $[20] !== productPriceAmount || $[21] !== recurringOptions || $[22] !== t) {
    t5 = !recurringOptions && <InfoRow label={t("dashboard.price_plan.payment_amount")}>{productPriceAmount ? productPriceAmount : formattedAmount}</InfoRow>;
    $[19] = formattedAmount;
    $[20] = productPriceAmount;
    $[21] = recurringOptions;
    $[22] = t;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== pricePlan.recurringOptions || $[25] !== recurringOptions || $[26] !== t || $[27] !== tICU) {
    t6 = !!recurringOptions?.trialPeriod && <InfoRow label={t("dashboard.price_plan.trial_period")}>{`${recurringOptions.trialPeriod} ${tICU(SubscriptionIntervalTitlesIcu[pricePlan.recurringOptions.trialInterval], {
        count: pricePlan.recurringOptions.trialPeriod
      })}`}</InfoRow>;
    $[24] = pricePlan.recurringOptions;
    $[25] = recurringOptions;
    $[26] = t;
    $[27] = tICU;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== recurringOptions || $[30] !== t) {
    t7 = recurringOptions?.limitOfPayments && <InfoRow label={t("dashboard.price_plan.limit_of_payments")}>{recurringOptions.limitOfPayments}</InfoRow>;
    $[29] = recurringOptions;
    $[30] = t;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== formattedAmount || $[33] !== recurringOptions || $[34] !== t) {
    t8 = recurringOptions && <InfoRow label={t("dashboard.price_plan.payment_amount")}>{`${formattedAmount} ${t("global.each").toLowerCase()} ${recurringOptions.intervalCount} ${recurringOptions.interval}`}</InfoRow>;
    $[32] = formattedAmount;
    $[33] = recurringOptions;
    $[34] = t;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== customRow || $[37] !== t2 || $[38] !== t3 || $[39] !== t4 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7 || $[43] !== t8) {
    t9 = <div className="flex max-w-sm flex-col items-start gap-2">{t2}{t3}{t4}{t5}{t6}{t7}{t8}{customRow}</div>;
    $[36] = customRow;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== children || $[46] !== stayOnHover || $[47] !== t9) {
    t10 = <Tooltip mode="hover" stayOnHover={stayOnHover} className="w-full max-w-fit" wrapperClassName="w-full" label={t9}>{children}</Tooltip>;
    $[45] = children;
    $[46] = stayOnHover;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  return t10;
};